import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import { SchoolEvent } from 'Src/models/event';
import { CLUB_STAFF } from 'Src/consts/club';

interface Props {
  event: SchoolEvent;
  details: any;
}

const TEXT_ROWS = [
  { title: 'Short event description', field: 'shortDescription' },
  { title: 'Event description', field: 'description', html: true },
  { title: 'Kit notes', field: 'kitNotes', html: true },
  { title: 'Comments', field: 'comments', html: true },
  { title: 'Transport', field: 'transportDetails', html: true }
];

const TIME_ROWS = [
  { title: 'Team departs', field: 'teamDeparts' },
  { title: 'Team returns', field: 'teamReturns' },
  { title: 'Meet time', field: 'meetTime' },
  { title: 'Tea time', field: 'teaTime' },
  { title: 'Lunch time', field: 'lunchTime' }
];

function plainText(value: unknown): string {
  return String(value || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

function staffNames(details: any, role: string): string {
  return propz
    .get<any[]>(details, ['staff'], [])
    .filter(staff => staff.staffRole === role)
    .map(staff =>
      [staff.firstName, staff.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
    )
    .filter(Boolean)
    .join('\n');
}

export function hasVisibleEventDetails(event: SchoolEvent, details: any): boolean {
  const textValues = TEXT_ROWS.map(row => propz.get(details, [row.field], ''));
  const timeValues = TIME_ROWS.map(row => propz.get(details, [row.field]));
  const staff = propz.get<any[]>(details, ['staff'], []);
  const sportDescription = propz.get(event, ['sport', 'description'], '');

  return (
    textValues.some(value => plainText(value) !== '') ||
    timeValues.some(value => Boolean(value)) ||
    staff.length > 0 ||
    plainText(sportDescription) !== ''
  );
}

export const EventDetailsSummary = ({ event, details }: Props) => {
  const rows: Array<{ title: string; value: React.ReactNode }> = [];

  TEXT_ROWS.forEach(row => {
    const rawValue = propz.get(details, [row.field], '');
    if (!plainText(rawValue)) return;
    rows.push({
      title: row.title,
      value: row.html ? <div dangerouslySetInnerHTML={{ __html: String(rawValue) }} /> : String(rawValue)
    });
  });

  const membersOfStaff = staffNames(details, CLUB_STAFF.MEMBER_OF_STAFF);
  const coaches = staffNames(details, CLUB_STAFF.COACH);
  if (membersOfStaff) rows.push({ title: 'Members of staff', value: membersOfStaff });
  if (coaches) rows.push({ title: 'Coach', value: coaches });

  TIME_ROWS.forEach(row => {
    const value = propz.get(details, [row.field]);
    if (!value) return;
    rows.push({ title: row.title, value: moment(value).format('hh:mm A') });
  });

  const sportDescription = propz.get(event, ['sport', 'description'], '');
  if (plainText(sportDescription)) rows.push({ title: 'Sport description', value: sportDescription });

  return (
    <table className="table table-sm mPre">
      <tbody>
        {rows.map(row => (
          <tr key={row.title}>
            <td className="w-25">{row.title}</td>
            <td className="w-75 mPreWrap">{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
